import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useServer } from '../contexts/ServerContext';
import { useNavigate } from 'react-router-dom';
import { get, set } from 'idb-keyval';
import { api } from '../contexts/ServerContext';
import { steamId64ToV2 } from '../utils/helpers';

const MAX_COMMENT_LENGTH = 300;
const MAX_NOTE_LENGTH = 500;
const NOTE_KEY_PREFIX = 'notes_';

function getNoteKey(server, steamId) {
  return `${NOTE_KEY_PREFIX}${server}_${steamId}`;
}

export default function CommentsTab({ steamId, isOwnPage }) {
  const { user } = useAuth();
  const { server } = useServer();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [page, setPage] = useState(1);
  const COMMENTS_PER_PAGE = 5;

  useEffect(() => {
    setLoading(true);
    api.get(`/api/players/${steamId}/comments`, { params: {} })
      .then(res => setComments(res.data.comments || []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [server, steamId]);

  useEffect(() => {
    setPage(1);
  }, [comments.length]);

  const canDelete = user && ['curator', 'yufuadmin', 'superadmin'].includes(user.rank) && !isOwnPage;

  const handleAdd = async () => {
    setError('');
    const text = input.trim();
    if (!text) return;
    if (text.length > MAX_COMMENT_LENGTH) {
      setError(`Максимум ${MAX_COMMENT_LENGTH} символов`);
      return;
    }
    setAdding(true);
    try {
      await api.post(`/api/players/${steamId}/comments`, { text }, { params: {} });
      const res = await api.get(`/api/players/${steamId}/comments`, { params: {} });
      setComments(res.data.comments || []);
      setInput('');
      setPage(1);
    } catch (e) {
      setError(e?.response?.data?.error || 'Ошибка при добавлении');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/api/players/${steamId}/comments/${id}`, { params: {} });
      setComments(comments.filter(c => c.id !== id));
    } catch {}
    setDeleting(null);
  };

  // Функция для рендера текста комментария с хэштегами и картинками
  function renderCommentWithMeta(text) {
    const imageRegex = /(?:^|\s|[@.,;:])((https?:\/\/[^\s]+?\.(jpg|jpeg|png|gif)))/gi;
    const hashtagRegex = /#[\\p{L}\\d_]+/gu;
    const parts = [];
    let lastIndex = 0;
    let match;
  
    // Ищем картинки
    while ((match = imageRegex.exec(text)) !== null) {
      // Текст до картинки
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      // Саму картинку (убираем лишние символы)
      const url = match[1].replace(/^[@.,;:\\s]+/, '');
      console.log(url)
      parts.push(
        <div key={match.index} className="my-2">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt="img" className="max-w-xs max-h-60 rounded border border-gmod-lighterer" />
          </a>
        </div>
      );
      lastIndex = match.index + match[0].length;
    }
    // Остаток текста
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
  
    // Подсвечиваем хэштеги в каждом текстовом куске
    return parts.flatMap((part, i) => {
      if (typeof part !== 'string') return part;
      const elements = [];
      let last = 0;
      for (const m of part.matchAll(hashtagRegex)) {
        if (m.index > last) elements.push(part.slice(last, m.index));
        elements.push(
          <span key={i + '-' + m.index} className="text-theme-primary font-semibold">{m[0]}</span>
        );
        last = m.index + m[0].length;
      }
      if (last < part.length) elements.push(part.slice(last));
      return elements.length ? elements : part;
    });
  }

  // Пагинация
  const totalPages = Math.max(1, Math.ceil(comments.length / COMMENTS_PER_PAGE));
  const pagedComments = comments.slice((page - 1) * COMMENTS_PER_PAGE, page * COMMENTS_PER_PAGE);

  return (
    <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer p-6">
      <div className="text-xl font-semibold text-theme-primary mb-4">Комментарии</div>
      <div className="mb-4">
        <textarea
          className="w-full p-2 rounded bg-gmod-lighter text-gmod-white resize-none"
          rows={3}
          maxLength={MAX_COMMENT_LENGTH}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Добавить комментарий..."
        />
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs text-gmod-white opacity-60">{input.length}/{MAX_COMMENT_LENGTH}</span>
          <button
            className="px-4 py-1 bg-theme-primary text-gmod-white rounded disabled:opacity-50"
            onClick={handleAdd}
            disabled={!input.trim() || input.length > MAX_COMMENT_LENGTH || adding}
          >Добавить</button>
        </div>
        {error && <div className="text-red-400 text-xs mt-1">{error}</div>}
      </div>
      {loading ? (
        <div className="text-gmod-white opacity-60">Загрузка...</div>
      ) : comments.length === 0 ? (
        <div className="text-gmod-white opacity-60">Комментариев нет</div>
      ) : (
        <>
          <ul className="space-y-3">
            {pagedComments.map(c => (
              <li key={c.id} className="bg-gmod-lighter rounded p-3 flex flex-col">
                <div className="flex items-center justify-between mb-1">
                  <span
                    className="font-semibold text-theme-primary hover:underline cursor-pointer"
                    onClick={() => navigate(`/players/${steamId64ToV2(c.authorSteamId)}`)}
                  >
                    {c.authorName}
                  </span>
                  <span className="text-xs text-gmod-white opacity-60">{new Date(c.createdAt).toLocaleString('ru-RU')}</span>
                  {canDelete && (
                    <button className="text-red-400 ml-2 text-xs" onClick={() => handleDelete(c.id)} disabled={deleting === c.id}>
                      {deleting === c.id ? '...' : 'Удалить'}
                    </button>
                  )}
                </div>
                <div className="text-gmod-white break-words whitespace-pre-line">{renderCommentWithMeta(c.text)}</div>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                className="px-3 py-1 bg-gmod-lighter text-gmod-white rounded disabled:opacity-50"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >Назад</button>
              <span className="text-gmod-white">Страница {page} из {totalPages}</span>
              <button
                className="px-3 py-1 bg-gmod-lighter text-gmod-white rounded disabled:opacity-50"
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
              >Вперёд</button>
            </div>
          )}
        </>
      )}
    </div>
  );
} 